import React from "react";
import Title from "../../ui/Title";

const GenderInequality = () => {
  return (
    <div>
      <Title
        title="How can the gender inequality in care be addressed?"
        className="mb-6"
      />

      <div className="space-y-4 text-softBlack leading-loose text-base font-normal">
        <p>
          According to an{" "}
          <a
            href="https://odi.org/en/insights/five-ways-to-change-gender-norms-in-unpaid-care-and-domestic-work/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            explainer
          </a>{" "}
          by the Overseas Development Institute (ODI), “Gender norms are
          complicated and in constant flux and changing them can take
          considerable time, risk and backlash.”
        </p>
        <p>
          Drawing on learnings from the WE-Care (Women’s Economic Empowerment
          and Care) project, the ODI has identified five recommendations to help
          programs and projects facilitate gender norm change.
        </p>
        <ol className="space-y-3">
          <li>
            <i>1. Use interactive approaches to foster reflection</i>
            <p className="mt-1">
              Interactive and participatory workshops, community and household
              dialogues, or communication tools act as ways to reflect amongst
              community members, and are substantially more effective than
              directive messages.
            </p>
          </li>
          <li>
            <i>
              2. Messages must be shared regularly and through a range of
              channels
            </i>
            <p className="mt-1">
              Messages intended for influencing gender norms need to be
              repeated, regular, and must be tailored to different target groups
              such as, youths, elders, religious and local leaders, etc.
              Different approaches can be used, like drama or street theatre,
              conversations, posters, leaflets, training, community-based
              dialogues, public events, and more.
            </p>
          </li>
          <li>
            <i>
              3. Identify and work with members and role models from the
              community
            </i>
            <p className="mt-1">
              Individuals who are trusted and respected in the community can act
              as role models to share new messages. Care should be taken so that
              the information they receive is not forgotten, diluted, or
              unintentionally changed.
            </p>
          </li>
          <li>
            <i>
              4. Engage men, but recognize that big changes happen in small
              steps
            </i>
            <p className="mt-1">
              It’s important to engage men and make sure that they participate
              regularly in care and domestic work. Change, when broken down into
              small and easy actions, can be more effective.
            </p>
          </li>
          <li>
            <i>5. Support long-term change through policy reform</i>
            <p className="mt-1">
              Advocacy with the government and policy-level changes are also key
              to achieving better results and long-term change.
            </p>
          </li>
        </ol>
        <p>
          An{" "}
          <a
            href="https://jamanetwork.com/journals/jama-health-forum/fullarticle/2805890"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue underline"
          >
            article
          </a>{" "}
          on unpaid caregiving published in 2023 highlights that women are less
          likely to receive assistance from family and friends or to use
          external caregiving services because caregiving is often viewed as a
          natural part of women’s work in society. Therefore, there is a need
          for a higher intensity of caregiver support for women and specific
          outreach efforts to ensure acceptability and accessibility. Similarly,
          effective workplace interventions and caregiver-friendly policies are
          also key to addressing the gender gap in caregiving. This enables
          caregivers to manage their care responsibilities without compromising
          financial stability or career growth. Workplace interventions can be
          in the form of flexible working hours, employee assistance programs
          that involve support groups, counseling, and skilling workshops.
        </p>
        <p className="italic">
          Note: The current research does not cover genders beyond women and
          men. One of our observations is that most existing resources address
          the gender gap in care primarily within the male–female gender binary,
          without considering the full spectrum of gender identities.
        </p>
      </div>
    </div>
  );
};

export default GenderInequality;
